<!-- Per-coin «Торговля» switches — arm/disarm both TFs (paper) or live unit. -->
<script>
  import { onMount, createEventDispatcher } from 'svelte'
  import {
    getPaperTrading,
    setPaperTradingEnabled,
    getLiveTrading,
    setLiveTradingEnabled,
  } from '../api.js'
  import { OPS_ERROR_RU } from '../lib/format.js'

  /** @type {'paper' | 'live'} */
  export let mode = 'paper'
  /** Compact row for Hub / Brain */
  export let compact = false

  const dispatch = createEventDispatcher()

  let assets = []
  let loading = true
  let busy = ''
  let err = ''
  let msg = ''
  let mutexBanner = ''

  async function refresh() {
    err = ''
    mutexBanner = ''
    try {
      const { res, data } = mode === 'live' ? await getLiveTrading() : await getPaperTrading()
      if (res?.ok && data?.ok && Array.isArray(data.assets)) {
        assets = data.assets
        const mx = data.wallet_mutex
        if (mode === 'live' && mx && (mx.peer_on || mx.peer_unknown) && mx.error_ru) {
          mutexBanner = mx.error_ru
        }
      } else {
        err = (data && data.error) || 'trading_status_failed'
      }
    } catch {
      err = 'trading_status_failed'
    } finally {
      loading = false
    }
  }

  /**
   * @param {string} asset
   * @param {boolean} next
   */
  async function onToggle(asset, next) {
    if (busy) return
    busy = asset
    err = ''
    msg = ''
    const prev = assets.map((a) => ({ ...a }))
    assets = assets.map((a) =>
      a.asset === asset ? { ...a, armed: next, enabled: next, partial: false } : a,
    )
    try {
      const r =
        mode === 'live'
          ? await setLiveTradingEnabled(asset, next)
          : await setPaperTradingEnabled(asset, next)
      if (!r.ok) {
        assets = prev
        const code = r.error || 'systemctl_failed'
        err = r.error_ru || OPS_ERROR_RU[code] || code
        return
      }
      if (r.data && r.data.state) {
        assets = assets.map((a) =>
          a.asset === asset ? { ...a, ...r.data.state, asset } : a,
        )
      } else {
        await refresh()
      }
      msg = (r.data && r.data.note_ru) || (next ? 'Торговля вкл' : 'Торговля выкл')
      dispatch('changed', { asset, enabled: next, mode })
    } catch {
      assets = prev
      err = 'Сбой запроса'
    } finally {
      busy = ''
    }
  }

  onMount(() => {
    refresh()
  })

  export function reload() {
    return refresh()
  }
</script>

<section
  class="coin-trade"
  class:coin-trade--compact={compact}
  aria-label={mode === 'live' ? 'Торговля live по монетам' : 'Торговля paper по монетам'}
>
  <header class="coin-trade__head">
    <h2 class="coin-trade__title">Торговля</h2>
    <p class="coin-trade__hint">
      {#if mode === 'live'}
        ETH 15m · OFF = coin-gate + stop unit · без confirm
      {:else}
        Одна монета · пауза 5m+15m
      {/if}
    </p>
  </header>

  {#if loading}
    <p class="pm-empty">Статус…</p>
  {:else if !assets.length}
    <p class="pm-empty">Нет монет</p>
  {:else}
    <div class="coin-trade__row">
      {#each assets as a (a.asset)}
        <div
          class="coin-trade__cell"
          class:is-off={!a.armed}
          class:is-partial={a.partial}
          data-asset={a.asset}
        >
          <span class="coin-trade__asset">{String(a.asset).toUpperCase()}</span>
          <label class="coin-trade__switch" title={a.armed ? 'Выключить торговлю' : 'Включить торговлю'}>
            <input
              type="checkbox"
              role="switch"
              aria-label={`Торговля ${String(a.asset).toUpperCase()}`}
              checked={!!a.armed}
              disabled={!!busy}
              on:change|preventDefault={() => onToggle(a.asset, !a.armed)}
            />
            <span class="coin-trade__ui" aria-hidden="true"></span>
            <span class="coin-trade__txt">{a.armed ? (a.partial ? 'частично' : 'вкл') : 'выкл'}</span>
          </label>
        </div>
      {/each}
    </div>
  {/if}

  {#if mutexBanner}
    <p class="coin-trade__mutex" role="status">{mutexBanner}</p>
  {/if}
  {#if msg}
    <p class="coin-trade__msg">{msg}</p>
  {/if}
  {#if err}
    <p class="pm-error">{OPS_ERROR_RU[err] || err}</p>
  {/if}
</section>

<style>
  .coin-trade {
    margin: 0 0 1rem;
  }
  .coin-trade__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.65rem;
  }
  .coin-trade__title {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .coin-trade__hint {
    margin: 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
  }
  .coin-trade__row {
    display: grid;
    gap: 0.55rem 0.75rem;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .coin-trade--compact .coin-trade__row {
    grid-template-columns: repeat(auto-fit, minmax(7.5rem, 1fr));
  }
  @media (max-width: 640px) {
    .coin-trade__row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  .coin-trade__cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.55rem 0.7rem;
    border-radius: 10px;
    border: 1px solid var(--pm-line);
    background: var(--pm-elevated-bg, transparent);
  }
  .coin-trade__cell.is-off {
    opacity: 0.72;
  }
  .coin-trade__cell.is-partial {
    border-color: var(--pm-warn, #b8860b);
  }
  .coin-trade__asset {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--pm-ink);
  }
  .coin-trade__switch {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    user-select: none;
  }
  .coin-trade__switch input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }
  .coin-trade__ui {
    width: 1.7rem;
    height: 0.95rem;
    border-radius: 999px;
    background: rgba(127, 127, 127, 0.35);
    position: relative;
    flex: 0 0 auto;
  }
  .coin-trade__ui::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s ease;
  }
  .coin-trade__switch input:checked + .coin-trade__ui {
    background: var(--pm-ok, #1a7f4e);
  }
  .coin-trade__switch input:checked + .coin-trade__ui::after {
    transform: translateX(0.72rem);
  }
  .coin-trade__txt {
    font-size: 0.64rem;
    color: var(--pm-ink-mute);
    min-width: 2.6rem;
  }
  .coin-trade__msg {
    margin: 0.5rem 0 0;
    font-size: 0.75rem;
    color: var(--pm-ink-soft);
  }
  .coin-trade__mutex {
    margin: 0.5rem 0 0;
    font-size: 0.78rem;
    line-height: 1.35;
    color: var(--pm-warn, #b8860b);
  }
</style>
